import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { PHILIPPINES_COUNTRY_CODE } from '@core';
import { STComponent, STColumn } from '@delon/abc/st';
import {
  dxEInvStatus,
  dxTinNo
} from '@shared-feature/components/dev-express/columns/dev-express-columns';
import { LayoutManagementBase } from '@shared-feature/components/layout-management-base/layout-management-base';
import DevExpress from 'devextreme';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { CustomStore } from 'devextreme/common/data';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Column } from 'devextreme/ui/data_grid';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { lastValueFrom } from 'rxjs';
import { DateFormatService } from 'src/app/shared/services/date-format.service';

import DataSource = DevExpress.common.data.DataSource;

@Injectable()
export abstract class DevExpressGridBase extends LayoutManagementBase {
  protected dateFormatService = inject(DateFormatService);

  useDevExpress = signal(false);
  dxDataSource: any;
  dxColumns = signal<Array<Column & { country?: string }>>([]);
  stColumns = signal<STColumn[]>([]);
  pageSize = signal(10);
  dxGridSignal = signal<DxDataGridComponent | undefined>(undefined);
  stGridSignal = signal<STComponent | undefined>(undefined);
  dxGridInitialized = signal<boolean>(false);
  country = signal<string>('');

  // Get date format - use getter to always read fresh from settings
  protected get dateFormat(): string {
    return (
      this.dateFormatService.currentDateFormatValue ||
      this.dateFormatService.getDefaultDateFormat()
    );
  }

  abstract url(): string;

  abstract srv: any;

  apiResult: any;

  mappedDxColumns: Signal<Column[]> = computed(() => {
    const currentCountry = this.country();
    const dateFormat = this.dateFormat;

    // Filter by country if needed
    let filteredColumns = this.dxColumns();
    if (currentCountry) {
      filteredColumns = filteredColumns.filter(col => {
        if (!col?.country) {
          return true;
        }
        return col.country === currentCountry;
      });
    }

    // Apply date / datetime format to columns that need it
    let columnsWithFormat = this.applyDateFormat(filteredColumns, dateFormat);

    // Apply special logic for Philippines
    if (currentCountry === PHILIPPINES_COUNTRY_CODE) {
      // Filter out e-inv status column for PH country
      columnsWithFormat = columnsWithFormat.filter(col => {
        return col.dataField !== dxEInvStatus().dataField;
      });

      // Apply special logic for Philippines TIN
      columnsWithFormat = columnsWithFormat.map(col => {
        if (col.dataField === dxTinNo().dataField) {
          return {
            ...col,
            calculateCellValue: (rowData: any) => {
              const registrationNo =
                rowData['registrationNo'] || rowData['registrationNumber'];
              const tinBranchCode = rowData['tinBranchCode'];

              if (registrationNo && tinBranchCode) {
                return `${registrationNo} (${tinBranchCode})`;
              } else if (registrationNo) {
                return registrationNo;
              } else if (tinBranchCode) {
                return `(${tinBranchCode})`;
              }
              return '';
            }
          };
        }
        return col;
      });
    }

    return columnsWithFormat;
  });

  /**
   * Apply date format to all date columns that don't already have a format
   */
  private applyDateFormat(columns: Column[], dateFormat: string): Column[] {
    const dateTimeFormat = this.dateFormatService.currentDateTimeFormatValue;
    return columns.map(col => {
      if (col.dataType === 'date' && !col.format) {
        return { ...col, format: dateFormat };
      }
      if (col.dataType === 'datetime' && !col.format) {
        return { ...col, format: dateTimeFormat };
      }
      return col;
    });
  }

  onDxGridInitialized(e: any) {
    this.dxGridSignal.set(e.component);
    this.dxGridInitialized.set(true); // Mark grid as initialized

    // Configure DevExpress locale for date formatting
    this.dateFormatService.configureDevExpressLocale();

    // Ensure refresh is called after initialization
    setTimeout(() => {
      e.component?.refresh?.();
    }, 0);
  }

  onDxSelectionChanged(e: any, dxGrid?: DxDataGridComponent) {
    const currentData = dxGrid?.instance.getDataSource().items() || [];

    const selectedRows = currentData?.filter((c: { checked?: boolean }) => !!c?.checked);

    // Update checked state locally
    currentData.forEach((item: any) => {
      item.checked = !!selectedRows.find((r: any) => r.id === item.id);
    });

    if (this.srv) {
      // Notify selection with current data
      this.srv.notifySelection(currentData);
    }

    // Refresh grid UI without fetching data
    dxGrid?.instance.repaint();
  }

  onDxCheckboxChanged(e: any, data: any, dxGrid?: DxDataGridComponent) {
    data.checked = e.value;
    // Notify selection with current grid data (no reload)
    const currentData = dxGrid?.instance.getDataSource().items() || [];
    this.srv.notifySelection(currentData);
    dxGrid?.instance.repaint();
  }

  filterConfig = {
    allowFiltering: true,
    allowHeaderFiltering: false,
    allowSorting: true,
    allowGrouping: true
  };

  onDxExporting(e: any) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.getComponentName());
    const gridInstance = e.component;

    exportDataGrid({
      component: gridInstance,
      worksheet: worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (!gridCell?.column?.dataType || !gridCell.value) return;

        if (gridCell.column.dataType === 'date') {
          // Only override the display format; DevExpress already sets the correct
          // UTC-adjusted Date value on excelCell before customizeCell is called.
          excelCell.numFmt = this.dateFormatService.getExcelDateFormat();
        }

        if (gridCell.column.dataType === 'datetime') {
          excelCell.numFmt = this.dateFormatService.getExcelDateTimeFormat();
        }
      }
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, `${this.getComponentName()}.xlsx`);
      });
    });

    e.cancel = true;
  }

  onGridTypeChange(st?: STComponent, dxGrid?: DxDataGridComponent) {
    if (this.useDevExpress()) {
      if (this.dxGridInitialized() && this.dxGridSignal()?.instance) {
        !!dxGrid
          ? dxGrid?.instance?.refresh()
          : this.dxGridSignal()?.instance.refresh();
      }
    } else {
      setTimeout(() => {
        st?.reload();
      }, 0);
    }
  }

  defaultParams(): any {
    const defaultParams = {
      skip: 0,
      take: this.pageSize() || 10,
      requireTotalCount: true,
      sort: [
        { selector: 'docDate', desc: true },
        { selector: 'docCode', desc: true }
      ],
      expand:
        'Supplier($select=Code),Currency($select=Code),Purchaser($select=Code)'
    };
    return defaultParams;
  }

  // Handle Select All checkbox change
  onSelectAllChanged(e: any, dxGrid?: DxDataGridComponent) {
    const currentData: any = dxGrid?.instance?.getDataSource()?.items() || [];
    currentData?.forEach((item: any) => {
      if (!item.disabled && !item.isGroup) {
        item.checked = e.value;
      }
    });

    // Notify selection with current data
    this.srv.notifySelection(currentData);
    // Refresh grid UI without fetching data
    dxGrid?.instance.repaint();
  }

  resetToDefaultTransactionView() {
    if (!this.srv) return;
    this.srv.transactionView = 0;
  }
  initDxDataSource() {
    this.resetToDefaultTransactionView();
    this.dxDataSource = new CustomStore({
      loadMode: 'processed',
      cacheRawData: false,
      load: async loadOptions => {
        try {
          const defaultParams = this.defaultParams();
          const requireTotalCount = loadOptions.requireTotalCount !== false;
          const loadSort = loadOptions.sort;
          const sort =
            Array.isArray(loadSort) && loadSort.length > 0
              ? loadSort
              : defaultParams.sort;
          const expand = defaultParams.expand;

          const queryParams = {
            ...loadOptions,
            requireTotalCount: requireTotalCount,
            sort: sort,
            expand: expand,
            filter: loadOptions.filter || null,
            search:
              loadOptions.searchOperation && loadOptions.searchValue
                ? {
                    value: loadOptions.searchValue,
                    operation: loadOptions.searchOperation
                  }
                : null
          };

          const separator = this.url().includes('?') ? '&' : '?';
          const url = `${this.url()}${separator}_silent=true`;

          const res = await lastValueFrom(
            this.http.post(url, {
              options: queryParams
            })
          );
          const data = res?.data || [];

          this.apiResult = res?.data;

          data.forEach((item: any) => {
            if (item.checked === undefined) {
              item.checked = false;
            }
          });

          return {
            data: data,
            totalCount: res?.totalCount
          };
        } catch (error) {
          return {
            data: [],
            totalCount: 0
          };
        }
      }
    });
  }

  isAllSelected(grid?: DxDataGridComponent): boolean {
    if (!grid?.instance) {
      return false;
    }

    let currentData: any[] = [];

    // Check if getDataSource is available
    if (typeof grid.instance.getDataSource === 'function') {
      const dataSource = grid.instance.getDataSource();
      if (dataSource) {
        currentData = dataSource?.items() || [];
      }
    } else {
      const dataSourceOption: any = grid.instance.option('dataSource');
      if (Array.isArray(dataSourceOption)) {
        currentData = dataSourceOption;
      } else if (
        dataSourceOption &&
        typeof (dataSourceOption as DataSource).items === 'function'
      ) {
        currentData = (dataSourceOption as DataSource).items() || [];
      } else {
        return false;
      }
    }

    return (
      currentData.length > 0 && currentData.every((item: any) => !!item.checked)
    );
  }

  resetGridColumns(
    event: any,
    st?: STComponent | any,
    devExpressGrid?: DxDataGridComponent | any
  ) {
    // Update the service's transactionView for non-DevExpress views (0 = table, 1 = card)
    // For DevExpress view (2), we don't update it to preserve the previous view preference
    if (this.srv && event !== 2) {
      this.srv.transactionView = event;
    }

    if (event === 2) {
      const dxGrid = devExpressGrid || this.dxGridSignal();
      this.useDevExpress.set(true);
      this.onGridTypeChange(st, dxGrid);

      if (!dxGrid?.instance) return;
      dxGrid.instance.beginUpdate();
      dxGrid.instance.option('columns', this.dxColumns());
      dxGrid.instance.endUpdate();
      dxGrid.instance.refresh();
    } else {
      this.useDevExpress.set(false);
      this.onGridTypeChange(st, devExpressGrid);

      setTimeout(() => {
        st?.resetColumns();
      }, 150);
    }
  }

  protected override getResetColumns(columns?: any): any[] {
    return this.mappedDxColumns();
  }
}
